/**
 * Workflow Templates Page
 * Configure approval workflow stages and roles
 */

import React, { useState, useEffect } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import { workflowService } from "../services/api/workflow.service";
import { rolesService } from "../services/api/roles.service";
import type { Workflow, CreateWorkflowStageDto } from "../types/workflow.types";
import type { Role } from "../types/role.types";
import BusyOverlay from "../components/common/BusyOverlay";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import {
  showSuccessToast,
  showErrorToast,
  showDeleteConfirm,
} from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import "../styles/pages/Workflows.css";

export const Workflows: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Expanded workflows (to show/hide stages)
  const [expandedWorkflows, setExpandedWorkflows] = useState<number[]>([]);

  // New stage inputs for each workflow
  const [newStageInputs, setNewStageInputs] = useState<{
    [workflowId: number]: { name: string; roleId: number };
  }>({});
  const [stageValidationErrors, setStageValidationErrors] = useState<{
    [workflowId: number]: { name?: string; roleId?: string };
  }>({});

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Workflows", "Configure approval workflow stages and roles");
    fetchData();
  }, [setPageTitle]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const [workflowsData, rolesData] = await Promise.all([
        workflowService.getWorkflows(),
        rolesService.getRoles(),
      ]);

      // Fetch stages for each workflow
      if (Array.isArray(workflowsData)) {
        const workflowsWithStages = await Promise.all(
          workflowsData.map(async (workflow) => {
            try {
              const workflowDetails = await workflowService.getWorkflowById(
                workflow.id,
              );
              return workflowDetails;
            } catch (err) {
              console.error(
                `Failed to load details for workflow ${workflow.id}:`,
                err,
              );
              return { ...workflow, stages: [] };
            }
          }),
        );
        setWorkflows(workflowsWithStages);

        // Initialize new stage inputs
        const initialInputs: typeof newStageInputs = {};
        workflowsWithStages.forEach((w) => {
          initialInputs[w.id] = { name: "", roleId: 0 };
        });
        setNewStageInputs(initialInputs);
      } else {
        setWorkflows([]);
      }

      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkflow = (workflowId: number) => {
    setExpandedWorkflows((prev) =>
      prev.includes(workflowId)
        ? prev.filter((id) => id !== workflowId)
        : [...prev, workflowId],
    );
  };

  const handleAddStage = async (workflowId: number) => {
    const input = newStageInputs[workflowId];
    const errors: { name?: string; roleId?: string } = {};

    if (!input?.name?.trim()) {
      errors.name = REQUIRED_FIELD_MESSAGE;
    }

    if (!input?.roleId) {
      errors.roleId = REQUIRED_FIELD_MESSAGE;
    }

    setStageValidationErrors((prev) => ({
      ...prev,
      [workflowId]: errors,
    }));

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const workflow = workflows.find((w) => w.id === workflowId);
      if (!workflow) return;

      const stageData: CreateWorkflowStageDto & { workflowId: number } = {
        workflowId,
        name: input.name,
        stageOrder: (workflow.stages?.length || 0) + 1,
        roleId: input.roleId,
        description: "",
        isActive: true,
        useSignature: false,
      };

      console.log("Attempting to create stage:", stageData);
      await workflowService.createStage(stageData);

      // Reset input
      setNewStageInputs((prev) => ({
        ...prev,
        [workflowId]: { name: "", roleId: 0 },
      }));
      setStageValidationErrors((prev) => ({
        ...prev,
        [workflowId]: {},
      }));

      await fetchData();
    } catch (err: any) {
      console.error("Add stage error:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    }
  };

  const handleDeleteStage = async (stageId: number, name: string) => {
    const confirmed = await showDeleteConfirm(name, "stage");

    if (confirmed) {
      try {
        await workflowService.deleteStage(stageId);
        await fetchData();
        showSuccessToast("Stage deleted successfully");
      } catch (err: any) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      }
    }
  };

  const handleMoveStage = async (
    workflowId: number,
    stageId: number,
    direction: "up" | "down",
  ) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow || !workflow.stages) return;

    const stageIndex = workflow.stages.findIndex((s) => s.id === stageId);
    if (stageIndex === -1) return;

    // Check boundaries
    if (direction === "up" && stageIndex === 0) return;
    if (direction === "down" && stageIndex === workflow.stages.length - 1)
      return;

    const currentStage = workflow.stages[stageIndex];
    const swapIndex = direction === "up" ? stageIndex - 1 : stageIndex + 1;
    const swapStage = workflow.stages[swapIndex];

    try {
      // Swap the stageOrder values
      const updatePayload1 = {
        id: currentStage.id,
        roleId: currentStage.roleId,
        name: currentStage.name,
        description: currentStage.description || "",
        stageOrder: swapStage.stageOrder,
        useSignature: currentStage.useSignature || false,
        paymentRequired: currentStage.paymentRequired || false,
        isActive: currentStage.isActive,
      };

      const updatePayload2 = {
        id: swapStage.id,
        roleId: swapStage.roleId,
        name: swapStage.name,
        description: swapStage.description || "",
        stageOrder: currentStage.stageOrder,
        useSignature: swapStage.useSignature || false,
        paymentRequired: swapStage.paymentRequired || false,
        isActive: swapStage.isActive,
      };

      await Promise.all([
        workflowService.updateStage(currentStage.id, updatePayload1),
        workflowService.updateStage(swapStage.id, updatePayload2),
      ]);

      await fetchData();
    } catch (err: any) {
      console.error("Move stage error:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    }
  };

  const handleTogglePaymentRequired = async (
    _workflowId: number,
    stageId: number,
    currentStage: any,
  ) => {
    try {
      const updateData = {
        id: stageId,
        roleId: currentStage.roleId,
        name: currentStage.name,
        description: currentStage.description || "",
        stageOrder: currentStage.stageOrder,
        useSignature: currentStage.useSignature || false,
        paymentRequired: !currentStage.paymentRequired,
        isActive: !currentStage.isActive,
      };
      await workflowService.updateStage(stageId, updateData);
      await fetchData();
    } catch (err: any) {
      console.error("Toggle required error:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    }
  };

  const handleToggleSignature = async (
    _workflowId: number,
    stageId: number,
    currentStage: any,
  ) => {
    try {
      const updateData = {
        id: stageId,
        roleId: currentStage.roleId,
        name: currentStage.name,
        description: currentStage.description || "",
        stageOrder: currentStage.stageOrder,
        useSignature: !currentStage.useSignature,
        paymentRequired: currentStage.paymentRequired || false,
        isActive: currentStage.isActive,
      };
      await workflowService.updateStage(stageId, updateData);
      await fetchData();
    } catch (err: any) {
      console.error("Toggle signature error:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    }
  };

  const handleDeleteWorkflow = async (
    workflowId: number,
    workflowName: string,
  ) => {
    const confirmed = await showDeleteConfirm(workflowName, "workflow");

    if (confirmed) {
      try {
        await workflowService.deleteWorkflow(workflowId);
        await fetchData();
        showSuccessToast("Workflow deleted successfully");
      } catch (err: any) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      }
    }
  };

  const getRoleName = (roleId: number) => {
    const role = roles.find((r) => String(r.id) === String(roleId));
    return role?.name || "Unknown Role";
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="workflows-page">
          <BusyOverlay isVisible={loading} message="Loading workflows..." />

          <div className="workflows-content">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            {/* Workflow Templates Section */}
            <div className="workflow-templates-section">
              <h2 className="section-title">Workflow Templates</h2>
              <p className="section-subtitle">
                Configure approval workflow stages and roles
              </p>

              {workflows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">📋</div>
                  <p className="empty-state__text">
                    No workflow templates found
                  </p>
                </div>
              ) : (
                <div className="workflow-templates-list">
                  {workflows.map((workflow) => (
                    <div key={workflow.id} className="workflow-template-card">
                      {/* Workflow Header */}
                      <div className="workflow-template-header">
                        <div className="workflow-template-info">
                          <h3 className="workflow-template-title">
                            {workflow.name}
                          </h3>
                          <div className="workflow-template-tags">
                            <span className="workflow-tag">
                              {workflow.module}
                            </span>
                          </div>
                          <p className="workflow-template-description">
                            {workflow.description}
                          </p>
                        </div>
                        <div className="workflow-template-actions">
                          <button
                            className="icon-btn"
                            onClick={() => toggleWorkflow(workflow.id)}
                            title={
                              expandedWorkflows.includes(workflow.id)
                                ? "Collapse"
                                : "Expand"
                            }
                          >
                            {expandedWorkflows.includes(workflow.id)
                              ? "▼"
                              : "▶"}
                          </button>
                          <button
                            className="icon-btn icon-btn--delete"
                            onClick={() =>
                              handleDeleteWorkflow(workflow.id, workflow.name)
                            }
                            title="Delete Workflow"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      {/* Workflow Stages - Show when expanded */}
                      {expandedWorkflows.includes(workflow.id) && (
                        <div className="workflow-stages-section">
                          <h4 className="stages-title">Approval Stages</h4>

                          {/* Existing Stages */}
                          <div className="stages-list">
                            {workflow.stages && workflow.stages.length > 0 ? (
                              workflow.stages.map((stage, index) => (
                                <div key={stage.id} className="stage-item">
                                  <div className="stage-reorder-buttons">
                                    <button
                                      className="icon-btn icon-btn--reorder"
                                      onClick={() =>
                                        handleMoveStage(
                                          workflow.id,
                                          stage.id,
                                          "up",
                                        )
                                      }
                                      disabled={index === 0}
                                      title="Move Up"
                                    >
                                      ⬆️
                                    </button>
                                    <button
                                      className="icon-btn icon-btn--reorder"
                                      onClick={() =>
                                        handleMoveStage(
                                          workflow.id,
                                          stage.id,
                                          "down",
                                        )
                                      }
                                      disabled={
                                        index === workflow.stages!.length - 1
                                      }
                                      title="Move Down"
                                    >
                                      ⬇️
                                    </button>
                                  </div>
                                  <div className="stage-number">
                                    {stage.stageOrder}
                                  </div>
                                  <div className="stage-content">
                                    <div className="stage-name">
                                      {stage.name}
                                    </div>
                                    <div className="stage-approver">
                                      Approver:{" "}
                                      {stage.roleName ||
                                        getRoleName(stage.roleId)}
                                    </div>
                                  </div>
                                  <div className="stage-required-toggle">
                                    <label className="toggle-label">
                                      <span className="toggle-text">
                                        {stage.paymentRequired
                                          ? "Payment Required"
                                          : "No Payment Required"}
                                      </span>
                                      <input
                                        type="checkbox"
                                        checked={stage.paymentRequired || false}
                                        onChange={() =>
                                          handleTogglePaymentRequired(
                                            workflow.id,
                                            stage.id,
                                            stage,
                                          )
                                        }
                                        className="toggle-checkbox"
                                      />
                                      <span className="toggle-slider"></span>
                                    </label>
                                  </div>
                                  <div className="stage-required-toggle">
                                    <label className="toggle-label">
                                      <span className="toggle-text">
                                        {stage.useSignature
                                          ? "Use Signature"
                                          : "Do Not Use Signature"}
                                      </span>
                                      <input
                                        type="checkbox"
                                        checked={stage.useSignature}
                                        onChange={() =>
                                          handleToggleSignature(
                                            workflow.id,
                                            stage.id,
                                            stage,
                                          )
                                        }
                                        className="toggle-checkbox"
                                      />
                                      <span className="toggle-slider"></span>
                                    </label>
                                  </div>
                                  <button
                                    className="icon-btn icon-btn--delete-small"
                                    onClick={() =>
                                      handleDeleteStage(stage.id, stage.name)
                                    }
                                    title="Delete Stage"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              ))
                            ) : (
                              <p className="no-stages-message">
                                No stages configured yet
                              </p>
                            )}
                          </div>

                          {/* Add New Stage */}
                          <div className="add-stage-form">
                            <input
                              type="text"
                              className={`stage-input ${stageValidationErrors[workflow.id]?.name ? "form-input--error" : ""}`}
                              placeholder="Stage name"
                              value={newStageInputs[workflow.id]?.name || ""}
                              onChange={(e) => {
                                setNewStageInputs((prev) => ({
                                  ...prev,
                                  [workflow.id]: {
                                    ...prev[workflow.id],
                                    name: e.target.value,
                                  },
                                }));
                                if (stageValidationErrors[workflow.id]?.name) {
                                  setStageValidationErrors((prev) => ({
                                    ...prev,
                                    [workflow.id]: {
                                      ...prev[workflow.id],
                                      name: undefined,
                                    },
                                  }));
                                }
                              }}
                            />
                            {stageValidationErrors[workflow.id]?.name && (
                              <div className="field-error">
                                {stageValidationErrors[workflow.id]?.name}
                              </div>
                            )}
                            <select
                              className={`role-select ${stageValidationErrors[workflow.id]?.roleId ? "form-input--error" : ""}`}
                              value={newStageInputs[workflow.id]?.roleId || 0}
                              onChange={(e) => {
                                setNewStageInputs((prev) => ({
                                  ...prev,
                                  [workflow.id]: {
                                    ...prev[workflow.id],
                                    roleId: parseInt(e.target.value),
                                  },
                                }));
                                if (
                                  stageValidationErrors[workflow.id]?.roleId
                                ) {
                                  setStageValidationErrors((prev) => ({
                                    ...prev,
                                    [workflow.id]: {
                                      ...prev[workflow.id],
                                      roleId: undefined,
                                    },
                                  }));
                                }
                              }}
                            >
                              <option value={0}>Select role</option>
                              {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                            {stageValidationErrors[workflow.id]?.roleId && (
                              <div className="field-error">
                                {stageValidationErrors[workflow.id]?.roleId}
                              </div>
                            )}
                            <button
                              className="btn-add-stage"
                              onClick={() => handleAddStage(workflow.id)}
                              title="Add Stage"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Workflows;
